import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useSession } from "@/stores/session";
import { useMeta } from "@/stores/meta";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/password-input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/auth-shell";

export const Route = createFileRoute("/login")({
  beforeLoad: async () => {
    const s = useSession.getState();
    if (!s.hydrated) { try { await s.hydrate(); } catch {} }
    if (useSession.getState().user) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

export function LoginPage() {
  const nav = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const meta = useMeta((s) => s.meta);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const me = await authApi.login(email, password);
      setSession(me);
      void nav({ to: "/" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setErr("Invalid credentials");
      else if (e instanceof ApiError && e.status === 403) {
        const msg = (e.body as { error?: string } | undefined)?.error;
        setErr(msg ?? "This account is not available.");
      } else setErr("Login failed. Please try again.");
    } finally { setLoading(false); }
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle={meta && (meta.cloud || !meta.devServer) ? "Welcome back." : "Welcome back to your dev server."}
      footer={<>No account? <a href="/signup" className="font-medium text-brand hover:underline">Sign up</a></>}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <PasswordInput id="password" name="password" required autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {err && <p role="alert" className="text-sm text-fail">{err}</p>}
        <Button type="submit" className="w-full bg-brand text-primary-foreground hover:bg-brand/90" disabled={loading}>
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
